import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

/**
 * Styled, focus-trapped confirmation modal. Replaces window.confirm for
 * money-moving actions. Shows an optional P&L line colored red on a loss.
 *
 * props:
 * - open         : bool — render when true
 * - title        : string — modal title
 * - message      : node   — body text
 * - details      : array of { label, value }  — optional spelled-out rows
 * - pnl          : { text, positive }          — optional P&L line (red when !positive)
 * - confirmLabel : string (default common.confirm)
 * - cancelLabel  : string (default common.cancel)
 * - danger       : bool — render confirm button as btn-danger (default true)
 * - onConfirm    : fn
 * - onCancel     : fn
 */
function ConfirmModal({ open, title, message, details, pnl, confirmLabel, cancelLabel, danger = true, onConfirm, onCancel }) {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const confirmRef = useRef(null);
    const dialogRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        // Move focus into the dialog and trap Tab within it.
        const node = confirmRef.current;
        if (node) node.focus();

        function onKeyDown(e) {
            if (e.key === 'Escape') { e.preventDefault(); onCancel && onCancel(); return; }
            if (e.key !== 'Tab') return;
            const focusables = dialogRef.current
                ? dialogRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
                : [];
            if (!focusables.length) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onCancel]);

    if (!open) return null;

    return (
        <div
            className="modal d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ background: 'rgba(0,0,0,0.6)' }}
            onClick={(e) => { if (e.target === e.currentTarget) onCancel && onCancel(); }}>
            <div className="modal-dialog modal-dialog-centered" ref={dialogRef}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className={`modal-title${danger ? ' text-danger' : ''}`}>{title}</h5>
                        <button type="button" className="btn-close" aria-label={t('common.cancel')} onClick={onCancel}></button>
                    </div>
                    <div className="modal-body">
                        {message ? <p className="mb-2">{message}</p> : null}
                        {details && details.length ? (
                            <ul className="list-unstyled mb-0">
                                {details.map((d, i) => (
                                    <li key={i} className="d-flex justify-content-between py-1">
                                        <span className="text-muted">{d.label}</span>
                                        <span className="num">{d.value}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                        {pnl ? (
                            <div className="d-flex justify-content-between py-1 border-top mt-2 pt-2">
                                <span className="text-muted">{t('positions.pnl')}</span>
                                <span className={`gh-confirm-pnl num ${pnl.positive ? 'gh-pos' : 'gh-neg'}`}>{pnl.text}</span>
                            </div>
                        ) : null}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onCancel}>
                            {cancelLabel || t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            ref={confirmRef}
                            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
                            onClick={onConfirm}>
                            {confirmLabel || t('common.confirm')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConfirmModal;
